import { useTranslation } from 'react-i18next';

import { addQuarters, format, isSameQuarter, parseISO, subQuarters } from 'date-fns';

import { getQuarterLabel } from '@/utils/getQuarterLabel';

import type { EstateAnalysis } from './analytics';

function getChartQuarterLabels(): string[] {
  const today = new Date();
  return Array.from({ length: 6 }, (_, i) => {
    const date = addQuarters(today, i - 3);
    return getQuarterLabel(date);
  });
}

type Valuation = EstateAnalysis['valuations'][number]['data']['valuations'][number][number];

type SaleValuation = Valuation & {
  quarter: string;
  salePrice: number;
};

type RentValuation = Valuation & {
  quarter: string;
  rentGross: number;
};

export type ValuationChange = {
  changeValue: number;
  changePercent: string;
  label: string;
};

export type ValuationChartData = {
  quarter: string;
  value: number;
  index: number;
  prevQuarterLabel?: string;
  prevQuarterChangePercent?: number;
};

export type ValuationValueChange = {
  label: string;
  value: number;
  changePercent: string;
};

type ValuationForecast = {
  label: string;
  confidence?: string;
  rentValuation:
    | {
        changeValue: number;
        changePercent: string;
      }
    | undefined;
  saleValuation:
    | {
        changeValue: number;
        changePercent: string;
        label: string;
      }
    | undefined;
};

export const useValuations = (
  data: EstateAnalysis
): {
  currentQuarterRentValuation?: RentValuation;
  currentQuarterSaleValuation?: SaleValuation;
  prevQuarterSaleValuationComparison?: ValuationChange;
  prevQuarterRentValuationComparison?: ValuationChange;
  prev3YQuarterSaleValuationComparison?: ValuationChange;
  prev3YQuarterRentValuationComparison?: ValuationChange;
  nextQuarterForecast?: ValuationForecast;
  highestSalesPriceRange?: ValuationValueChange;
  lowestSalesPriceRange?: ValuationValueChange;
  highestRentalPriceRange?: ValuationValueChange;
  lowestRentalPriceRange?: ValuationValueChange;
  salesPriceRangeChartData?: ValuationChartData[];
  rentalPriceRangeChartData?: ValuationChartData[];
  currentQuarterLabel?: string;
} => {
  const { t } = useTranslation();

  if (!data.valuations.length) {
    // If no data is available, return immediately
    return {};
  }

  const saleValuationResponse = data.valuations.find(({ dealType }) => dealType === 'sale')?.data
    ?.valuations?.[0];
  const rentValuationResponse = data.valuations.find(({ dealType }) => dealType === 'rent')?.data
    ?.valuations?.[0];
  if (!saleValuationResponse || !rentValuationResponse) {
    // If no data is available, return immediately
    return {};
  }

  const today = format(new Date(), 'yyyy-MM-dd');
  const todayIndex = data.valuations[0].data.valuations[0].findIndex(
    ({ date }) => date && isSameQuarter(parseISO(date), parseISO(today))
  );
  const currentQuarterLabel = getQuarterLabel(today);
  const saleValuationData: Array<SaleValuation> = saleValuationResponse.map((valuation) => ({
    ...valuation,
    salePrice: valuation.salePrice ?? 0,
    quarter: valuation.date ? getQuarterLabel(valuation.date) : '',
  }));
  const rentValuationData: Array<RentValuation> = rentValuationResponse.map((valuation) => ({
    ...valuation,
    rentGross: valuation.rentGross ?? 0,
    quarter: valuation.date ? getQuarterLabel(valuation.date) : '',
  }));

  const currentQuarterSaleValuation = saleValuationData[todayIndex];
  const currentQuarterRentValuation = rentValuationData[todayIndex];

  const prevQuarterSaleValuation = saleValuationData[todayIndex - 1];
  const nextQuarterSaleValuation = saleValuationData[todayIndex + 1];
  const prevQuarterRentValuation = rentValuationData[todayIndex - 1];
  const nextQuarterRentValuation = rentValuationData[todayIndex + 1];

  const prevQuarterLabel = getQuarterLabel(subQuarters(parseISO(today), 1));

  const prevQuarterSaleValuationComparison: ValuationChange = {
    changeValue: 0,
    changePercent: '-',
    label: prevQuarterLabel,
  };

  if (prevQuarterSaleValuation) {
    prevQuarterSaleValuationComparison.changeValue =
      currentQuarterSaleValuation.salePrice - prevQuarterSaleValuation.salePrice;
    prevQuarterSaleValuationComparison.changePercent = (
      ((currentQuarterSaleValuation.salePrice - prevQuarterSaleValuation.salePrice) * 100) /
      currentQuarterSaleValuation.salePrice
    ).toFixed(1);
  }

  const prevQuarterRentValuationComparison: ValuationChange = {
    changeValue: 0,
    changePercent: '-',
    label: prevQuarterLabel,
  };

  if (prevQuarterRentValuation) {
    prevQuarterRentValuationComparison.changeValue =
      currentQuarterRentValuation.rentGross - prevQuarterRentValuation.rentGross;
    prevQuarterRentValuationComparison.changePercent = (
      ((currentQuarterRentValuation.rentGross - prevQuarterRentValuation.rentGross) * 100) /
      currentQuarterRentValuation.rentGross
    ).toFixed(1);
  }

  const avgPrev3YSaleValuation = Math.round(
    [...saleValuationData]
      .slice(todayIndex - 12, todayIndex)
      .reduce((sum, el) => sum + el.salePrice, 0) / 12
  );

  const avgPrev3YRentValuation = Math.round(
    [...rentValuationData]
      .slice(todayIndex - 12, todayIndex)
      .reduce((sum, el) => sum + el.rentGross, 0) / 12
  );

  const prev3YQuarterSaleValuationComparison: ValuationChange = {
    changeValue: currentQuarterSaleValuation.salePrice - avgPrev3YSaleValuation,
    changePercent: (
      ((currentQuarterSaleValuation.salePrice - avgPrev3YSaleValuation) * 100) /
      currentQuarterSaleValuation.salePrice
    ).toFixed(1),
    label: t('overPast3Years'),
  };

  const prev3YQuarterRentValuationComparison: ValuationChange = {
    changeValue: currentQuarterRentValuation.rentGross - avgPrev3YRentValuation,
    changePercent: (
      ((currentQuarterRentValuation.rentGross - avgPrev3YRentValuation) * 100) /
      currentQuarterRentValuation.rentGross
    ).toFixed(1),
    label: t('overPast3Years'),
  };

  const nextQuarterLabel = getQuarterLabel(addQuarters(parseISO(today), 1));

  const nextQuarterForecast: ValuationForecast = {
    label: nextQuarterLabel,
    confidence: nextQuarterSaleValuation?.confidence,
    rentValuation: nextQuarterRentValuation
      ? {
          changeValue: currentQuarterRentValuation.rentGross - nextQuarterRentValuation.rentGross,
          changePercent: (
            ((currentQuarterRentValuation.rentGross - nextQuarterRentValuation.rentGross) * 100) /
            currentQuarterRentValuation.rentGross
          ).toFixed(1),
        }
      : undefined,
    saleValuation: nextQuarterSaleValuation
      ? {
          changeValue: currentQuarterSaleValuation.salePrice - nextQuarterSaleValuation.salePrice,
          changePercent: (
            ((currentQuarterSaleValuation.salePrice - nextQuarterSaleValuation.salePrice) * 100) /
            currentQuarterSaleValuation.salePrice
          ).toFixed(1),
          label: nextQuarterLabel,
        }
      : undefined,
  };

  const last4QSalesValuation = [...saleValuationData].slice(todayIndex - 4, todayIndex);

  const { min: lowestLast4QSalesValuation, max: hightestLast4QSalesValuation } =
    last4QSalesValuation.reduce(
      (acc, el) => {
        if (el.salePrice > acc.max.salePrice) acc.max = el;
        if (el.salePrice < acc.min.salePrice) acc.min = el;
        return acc;
      },
      { min: last4QSalesValuation[0], max: last4QSalesValuation[0] }
    );

  const highestSalesPriceRange: ValuationValueChange = {
    label: hightestLast4QSalesValuation.quarter,
    value: hightestLast4QSalesValuation.salePrice,
    changePercent: (
      ((currentQuarterSaleValuation.salePrice - hightestLast4QSalesValuation.salePrice) * 100) /
      currentQuarterSaleValuation.salePrice
    ).toFixed(1),
  };

  const lowestSalesPriceRange: ValuationValueChange = {
    label: lowestLast4QSalesValuation.quarter,
    value: lowestLast4QSalesValuation.salePrice,
    changePercent: (
      ((currentQuarterSaleValuation.salePrice - lowestLast4QSalesValuation.salePrice) * 100) /
      currentQuarterSaleValuation.salePrice
    ).toFixed(1),
  };

  const last4QRentValuation = [...rentValuationData].slice(todayIndex - 4, todayIndex);
  const { min: lowestLast4QRentValuation, max: highestLast4QRentValuation } =
    last4QRentValuation.reduce(
      (acc, el) => {
        if (el.rentGross > acc.max.rentGross) acc.max = el;
        if (el.rentGross < acc.min.rentGross) acc.min = el;
        return acc;
      },
      { min: last4QRentValuation[0], max: last4QRentValuation[0] }
    );

  const highestRentalPriceRange: ValuationValueChange = {
    label: highestLast4QRentValuation.quarter,
    value: highestLast4QRentValuation.rentGross,
    changePercent: (
      ((currentQuarterRentValuation.rentGross - highestLast4QRentValuation.rentGross) * 100) /
      currentQuarterRentValuation.rentGross
    ).toFixed(1),
  };

  const lowestRentalPriceRange: ValuationValueChange = {
    label: lowestLast4QRentValuation.quarter,
    value: lowestLast4QRentValuation.rentGross,
    changePercent: (
      ((currentQuarterRentValuation.rentGross - lowestLast4QRentValuation.rentGross) * 100) /
      currentQuarterRentValuation.rentGross
    ).toFixed(1),
  };

  const chartQuarterLabels = getChartQuarterLabels();
  const salesPriceRangeChartData: Array<ValuationChartData> = [];
  const rentalPriceRangeChartData: Array<ValuationChartData> = [];
  for (let index = 0; index < chartQuarterLabels.length; index++) {
    const quarter = chartQuarterLabels[index];
    const saleValuation =
      saleValuationData.find((data) => data.quarter === quarter)?.salePrice || 0;
    const rentValuation =
      rentValuationData.find((data) => data.quarter === quarter)?.rentGross || 0;
    salesPriceRangeChartData.push({
      index,
      quarter,
      value: saleValuation,
      prevQuarterLabel: salesPriceRangeChartData[index - 1]?.quarter,
      prevQuarterChangePercent:
        index === 0 || saleValuation === 0
          ? undefined
          : ((saleValuation - salesPriceRangeChartData[index - 1].value) * 100) / saleValuation,
    });
    rentalPriceRangeChartData.push({
      index,
      quarter,
      value: rentValuation,
      prevQuarterLabel: rentalPriceRangeChartData[index - 1]?.quarter,
      prevQuarterChangePercent:
        index === 0 || saleValuation === 0
          ? undefined
          : ((rentValuation - rentalPriceRangeChartData[index - 1].value) * 100) / rentValuation,
    });
  }

  return {
    currentQuarterRentValuation,
    currentQuarterSaleValuation,
    prevQuarterSaleValuationComparison,
    prevQuarterRentValuationComparison,
    prev3YQuarterSaleValuationComparison,
    prev3YQuarterRentValuationComparison,
    nextQuarterForecast,
    highestSalesPriceRange,
    lowestSalesPriceRange,
    highestRentalPriceRange,
    lowestRentalPriceRange,
    salesPriceRangeChartData,
    rentalPriceRangeChartData,
    currentQuarterLabel,
  };
};
